<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Migration warnings -->
    <div
      v-if="migrationWarnings.length > 0"
      class="mx-2 mt-2 rounded border border-amber-500/40 bg-amber-900/30 px-3 py-2 text-xs text-amber-300 space-y-1 flex-shrink-0"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="font-semibold text-amber-400">Datasets updated — time axis moved to dataset level</div>
        <button
          class="text-amber-400 hover:text-white flex-shrink-0 mt-0.5"
          title="Dismiss"
          @click="store.clearMigrationWarnings()"
        >✕</button>
      </div>
      <div v-for="(w, i) in migrationWarnings" :key="i" class="leading-snug">{{ w }}</div>
    </div>

    <!-- Cross-cutting reference issues -->
    <div
      v-if="danglingRefs.length > 0 || missingDatasetWidgets.length > 0"
      class="mx-2 mt-2 rounded border border-amber-500/40 bg-amber-900/30 px-3 py-2 text-xs text-amber-300 space-y-1 flex-shrink-0"
    >
      <div class="font-semibold text-amber-400">Widget/Dataset issues</div>
      <button
        v-for="u in danglingRefs"
        :key="'d-' + u.objectUuid"
        class="block w-full text-left text-amber-300 hover:text-white transition-colors"
        @click="navigate(u)"
      >{{ capitalize(u.obj) }} {{ u.haspid }} references a Dataset that no longer exists</button>
      <button
        v-for="u in missingDatasetWidgets"
        :key="'m-' + u.objectUuid"
        class="block w-full text-left text-amber-300 hover:text-white transition-colors"
        @click="navigate(u)"
      >{{ capitalize(u.obj) }} {{ u.haspid }} has no Dataset linked — won't be exported</button>
    </div>

    <!-- Library list (always visible) -->
    <div class="flex items-center justify-between px-3 py-2 border-b border-slate-600 flex-shrink-0">
      <span class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Dataset Library</span>
      <button
        class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors"
        title="Create dataset"
        @click="createDataset"
      >+ New</button>
    </div>

    <ResourceListToolbar
      v-if="datasets.length > 0"
      :search="search"
      @update:search="search = $event"
      :sortKey="sortKey"
      @update:sortKey="sortKey = $event"
      :sortOptions="sortOptions"
      :filterValue="filterValue"
      @update:filterValue="filterValue = $event"
      :filterOptions="filterOptions"
      filterLabel="All types"
    />

    <div class="flex-1 overflow-y-auto">
      <div v-if="datasets.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No datasets yet.<br>
        Click <strong class="text-slate-400">+ New</strong> to create one.
      </div>
      <div v-else-if="filteredDatasets.length === 0" class="px-3 py-6 text-xs text-slate-500 text-center">
        No datasets match your search/filter.
      </div>

      <ResourceListItem
        v-for="ds in filteredDatasets"
        :key="ds.uuid"
        :idLabel="'#' + ds.id"
        :title="ds.name"
        :meta="rowMeta(ds)"
        :warnings="getWarnings(ds)"
        :used="isUsed(ds)"
        @select="openEditor(ds.uuid)"
      >
        <template #actions>
          <button
            class="text-slate-400 hover:text-cyan-300 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Duplicate"
            @click="duplicateDataset(ds.uuid)"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            class="text-slate-400 hover:text-red-400 p-1 rounded hover:bg-slate-600 transition-colors"
            title="Delete"
            @click="pendingDeleteUUID = ds.uuid"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </template>
      </ResourceListItem>
    </div>
  </div>

  <!-- Delete confirmation — no references: simple 2-button -->
  <ConfirmationModal
    :show="pendingDeleteUUID !== null && deleteUsages.length === 0"
    title="Delete Dataset"
    :message="pendingDeleteDataset ? `Are you sure you want to delete <strong>${pendingDeleteDataset.name}</strong>?` : 'Are you sure?'"
    @confirm="confirmDelete"
    @cancel="pendingDeleteUUID = null"
  />

  <!-- Delete confirmation — has references: list them, offer Find usages -->
  <Modal :show="pendingDeleteUUID !== null && deleteUsages.length > 0" @update:show="pendingDeleteUUID = null">
    <div class="flex">
      <img :src="'media/doc/attention2.png'" width="30" />
      <h3 class="ml-4 text-xl leading-6 font-medium text-white">Delete Dataset</h3>
    </div>
    <div class="mt-4 ml-4 text-gray-200 text-sm">
      <p>This Dataset is currently used by:</p>
      <ul class="list-disc list-inside my-2 space-y-0.5">
        <li v-for="u in deleteUsages" :key="u.objectUuid">{{ capitalize(u.obj) }} {{ u.haspid }}</li>
      </ul>
      <p>Delete anyway?</p>
    </div>
    <div class="mt-7 w-full flex justify-between gap-2">
      <form-button class="flex-1 py-2" color="danger" @click="confirmDelete">Delete anyway</form-button>
      <form-button class="flex-1 py-2" @click="findUsages">Find usages</form-button>
      <form-button class="flex-1 py-2" @click="pendingDeleteUUID = null">Cancel</form-button>
    </div>
  </Modal>

  <!-- Dataset editor — draggable floating popup (also the Inspector) -->
  <FloatingPopup
    id="dataset-editor"
    :modelValue="editingUUID !== null && editingDataset !== null"
    @update:modelValue="v => { if (!v) editingUUID = null }"
    :title="editingDataset?.name || 'Dataset'"
    :width="460"
    :height="680"
    :minwidth="380"
    :minheight="400"
    :position="{ x: 300, y: 80 }"
  >
    <DatasetEditor
      v-if="editingDataset"
      :dataset="editingDataset"
      :settings="settings"
      :objectlayer="objectlayer"
      @navigate-to-widget="navigate"
    />
  </FloatingPopup>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useDatasetStore, DATASET_TYPES } from '../../stores/datasetStore.js'
import {
  findWidgetsUsingDataset,
  findDatasetDrivenWidgetsWithoutDataset,
  findDanglingDatasetRefs,
  getDatasetWarnings,
} from '../../utils/datasetValidation.js'
import DatasetEditor from './DatasetEditor.vue'
import FloatingPopup from '../FloatingPopup.vue'
import ConfirmationModal from '../ConfirmationModal.vue'
import Modal from '../Modal.vue'
import ResourceListToolbar from '../resources/ResourceListToolbar.vue'
import ResourceListItem from '../resources/ResourceListItem.vue'

const props = defineProps({
  settings: { type: Object, required: true },
  objectlayer: { type: Object, default: null },
  requestOpenUUID: { type: String, default: null },
})
const emit = defineEmits(['navigate-to-widget'])

const store = useDatasetStore()
const datasets = computed(() => store.all)
const migrationWarnings = computed(() => store.migrationWarnings)

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

// --- Search / sort / filter ---

const search = ref('')
const sortKey = ref('name-asc')
const filterValue = ref('')

const sortOptions = [
  { value: 'name-asc',  label: 'Name ↑' },
  { value: 'name-desc', label: 'Name ↓' },
  { value: 'id-asc',    label: 'ID ↑' },
  { value: 'id-desc',   label: 'ID ↓' },
]
const filterOptions = DATASET_TYPES.map(t => ({ value: t.value, label: t.label }))

const filteredDatasets = computed(() => {
  let list = datasets.value
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(d => d.name.toLowerCase().includes(q) || String(d.id).includes(q))
  }
  if (filterValue.value) {
    list = list.filter(d => d.type === filterValue.value)
  }
  list = [...list]
  switch (sortKey.value) {
    case 'name-asc':  list.sort((a, b) => a.name.localeCompare(b.name)); break
    case 'name-desc': list.sort((a, b) => b.name.localeCompare(a.name)); break
    case 'id-asc':    list.sort((a, b) => a.id - b.id); break
    case 'id-desc':   list.sort((a, b) => b.id - a.id); break
  }
  return list
})

function rowMeta(ds) {
  const refs = props.objectlayer ? findWidgetsUsingDataset(props.objectlayer, ds.uuid).length : 0
  return [`${ds.series.length} series`, `${refs} reference${refs === 1 ? '' : 's'}`]
}

function getWarnings(ds) {
  return getDatasetWarnings(ds, store.all)
}

function isUsed(ds) {
  return props.objectlayer ? findWidgetsUsingDataset(props.objectlayer, ds.uuid).length > 0 : false
}

// --- Cross-cutting validation ---

const danglingRefs = computed(() =>
  props.objectlayer ? findDanglingDatasetRefs(props.objectlayer, store) : []
)
const missingDatasetWidgets = computed(() =>
  props.objectlayer ? findDatasetDrivenWidgetsWithoutDataset(props.objectlayer) : []
)

function navigate(u) {
  emit('navigate-to-widget', { objectUuid: u.objectUuid, pageUuid: u.pageUuid })
}

// --- Editor popup ---

const editingUUID = ref(null)
const editingDataset = computed(() => editingUUID.value ? store.getDataset(editingUUID.value) : null)

watch(() => props.requestOpenUUID, (uuid) => {
  if (uuid) editingUUID.value = uuid
})

function createDataset() {
  const uuid = store.addDataset()
  editingUUID.value = uuid
}

function openEditor(uuid) {
  editingUUID.value = uuid
}

function duplicateDataset(uuid) {
  const newUUID = store.duplicateDataset(uuid)
  if (newUUID) editingUUID.value = newUUID
}

// --- Delete workflow ---

const pendingDeleteUUID = ref(null)
const pendingDeleteDataset = computed(() => pendingDeleteUUID.value ? store.getDataset(pendingDeleteUUID.value) : null)
const deleteUsages = computed(() =>
  pendingDeleteUUID.value && props.objectlayer
    ? findWidgetsUsingDataset(props.objectlayer, pendingDeleteUUID.value)
    : []
)

function confirmDelete() {
  const uuid = pendingDeleteUUID.value
  pendingDeleteUUID.value = null
  if (editingUUID.value === uuid) editingUUID.value = null
  store.removeDataset(uuid)
}

function findUsages() {
  const uuid = pendingDeleteUUID.value
  pendingDeleteUUID.value = null
  editingUUID.value = uuid
}
</script>
